// src/components/Footer.jsx
import React, { useState, useEffect } from 'react';
import { 
  FaClock, 
  FaFacebookF, 
  FaTwitter, 
  FaInstagram, 
  FaYoutube, 
  FaPinterest, 
  FaLinkedinIn,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp,
  FaCircle
} from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import './Footer.css';
import leafImage from '../assets/images/footer-leaf.png';

const Footer = ({ tenant }) => {
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllHours, setShowAllHours] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const currentYear = new Date().getFullYear();
  const primaryColor = tenant?.primary_color || '#FF9F0D';

  useEffect(() => {
    if (tenant?.id) {
      loadContactData();
    } else {
      setLoading(false);
    }

    // Update time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, [tenant]);

  const loadContactData = async () => {
    try {
      const { data, error } = await supabase
        .from('website_sections')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('section_name', 'contact')
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data && data.content) {
        setContactData(data.content);
      }
    } catch (error) {
      console.error('Error loading contact data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get full business hours
  const getBusinessHours = () => {
    const defaultHours = {
      monday: '10:00 AM - 10:00 PM',
      tuesday: '10:00 AM - 10:00 PM',
      wednesday: '10:00 AM - 10:00 PM',
      thursday: '10:00 AM - 10:00 PM',
      friday: '10:00 AM - 11:00 PM',
      saturday: '10:00 AM - 11:00 PM',
      sunday: 'Closed'
    };

    return contactData?.hours || defaultHours;
  };

  // Check if restaurant is currently open
  const isOpenNow = () => {
    const hours = getBusinessHours();
    const today = currentTime.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const todayHours = hours[today];

    if (!todayHours || todayHours.toLowerCase() === 'closed') {
      return { open: false, message: 'Closed Today' };
    }

    try {
      const [openTime, closeTime] = todayHours.split(' - ');
      const currentHour = currentTime.getHours();
      const currentMinute = currentTime.getMinutes();
      
      // Parse opening time
      const openMatch = openTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!openMatch) return { open: false, message: todayHours };
      
      let openHour = parseInt(openMatch[1]);
      const openMin = parseInt(openMatch[2]);
      if (openMatch[3].toUpperCase() === 'PM' && openHour !== 12) openHour += 12;
      if (openMatch[3].toUpperCase() === 'AM' && openHour === 12) openHour = 0;
      
      // Parse closing time
      const closeMatch = closeTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!closeMatch) return { open: false, message: todayHours };
      
      let closeHour = parseInt(closeMatch[1]);
      const closeMin = parseInt(closeMatch[2]);
      if (closeMatch[3].toUpperCase() === 'PM' && closeHour !== 12) closeHour += 12;
      if (closeMatch[3].toUpperCase() === 'AM' && closeHour === 12) closeHour = 0;
      
      // Check if currently open
      const currentTotalMin = currentHour * 60 + currentMinute;
      const openTotalMin = openHour * 60 + openMin;
      const closeTotalMin = closeHour * 60 + closeMin;
      
      if (currentTotalMin >= openTotalMin && currentTotalMin <= closeTotalMin) {
        return { open: true, message: `Open until ${closeTime}` };
      } else if (currentTotalMin < openTotalMin) {
        return { open: false, message: `Opens at ${openTime}` };
      } else {
        return { open: false, message: 'Closed' };
      }
    } catch (error) {
      return { open: false, message: todayHours };
    }
  };

  const hours = getBusinessHours();
  const aboutText = contactData?.description || tenant?.description || 
    'Experience exceptional dining with us. We pride ourselves on delivering outstanding service and unforgettable culinary experiences.';
  const businessName = tenant?.business_name || 'Our Restaurant';

  // Get contact details
  const phone = contactData?.phone || tenant?.phone || '+91 98765 43210';
  const email = contactData?.email || tenant?.email || 'info@restaurant.com';
  const address = contactData?.address || tenant?.address || '123 Main Street';
  const city = contactData?.city || tenant?.city || 'Mumbai';
  const state = contactData?.state || tenant?.state || 'Maharashtra';
  const zipCode = contactData?.zip_code || '';
  const country = contactData?.country || 'India';
  
  const fullAddress = `${address}, ${city}, ${state}${zipCode ? ' ' + zipCode : ''}, ${country}`;

  // Social media links
  const socialLinks = [
    { platform: 'facebook', icon: FaFacebookF, url: contactData?.social_links?.facebook, color: '#1877F2' },
    { platform: 'twitter', icon: FaTwitter, url: contactData?.social_links?.twitter, color: '#1DA1F2' },
    { platform: 'instagram', icon: FaInstagram, url: contactData?.social_links?.instagram, color: '#E4405F' },
    { platform: 'youtube', icon: FaYoutube, url: contactData?.social_links?.youtube, color: '#FF0000' },
    { platform: 'linkedin', icon: FaLinkedinIn, url: contactData?.social_links?.linkedin, color: '#0A66C2' },
    { platform: 'pinterest', icon: FaPinterest, url: contactData?.social_links?.pinterest, color: '#E60023' }
  ].filter(link => link.url && link.url.trim() !== '');

  const showSocialLinks = !contactData || contactData.show_social_links !== false;
  const mapEmbedUrl = contactData?.map_embed_url;
  const showMapSection = contactData?.show_map !== false && mapEmbedUrl;

  // Get today's day
  const today = currentTime.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const todayHours = hours[today] || 'Closed';

  // Format day names
  const formatDay = (day) => day.charAt(0).toUpperCase() + day.slice(1);

  // Get status
  const status = isOpenNow();

  return (
    <footer className="footer">
      {/* Animated Background Gradient */}
      <div className="footer-gradient-bg"></div>

      {/* Decorative Leaf */}
      <div className="footer-leaf">
        <img src={leafImage} alt="" className="footer-leaf-img" />
      </div>

      <div className="footer-container">
        {/* Footer Top - Enhanced */}
        <div className="footer-top-enhanced">
          {/* Column 1: About Us */}
          <div className="footer-column footer-about">
            <div className="footer-brand">
              <h3 className="footer-brand-name" style={{ color: primaryColor }}>
                {businessName}
              </h3>
              <div className="footer-brand-divider" style={{ backgroundColor: primaryColor }}></div>
            </div>
            <p className="footer-about-text">{aboutText}</p>
            
            {/* Quick Stats */}
            <div className="footer-stats">
              <div className="footer-stat-item">
                <span className="footer-stat-icon" style={{ color: primaryColor }}></span>
                <span className="footer-stat-text">Premium Quality</span>
              </div>
              <div className="footer-stat-item">
                <span className="footer-stat-icon" style={{ color: primaryColor }}></span>
                <span className="footer-stat-text">Authentic Taste</span>
              </div>
            </div>
          </div>

          {/* Column 2: Contact Info */}
          <div className="footer-column footer-contact">
            <div className="footer-section-header">
              <h3 className="footer-column-title" style={{ color: primaryColor }}>
                Get In Touch
              </h3>
              <p className="footer-section-subtitle">We're here to serve you</p>
            </div>
            
            <div className="footer-contact-list">
              {/* Phone */}
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="footer-contact-item">
                <div className="footer-contact-icon" style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}>
                  <FaPhone />
                </div>
                <div className="footer-contact-info">
                  <span className="footer-contact-label">Call Us Anytime</span>
                  <span className="footer-contact-value">{phone}</span>
                </div>
              </a>

              {/* Email */}
              <a href={`mailto:${email}`} className="footer-contact-item">
                <div className="footer-contact-icon" style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}>
                  <FaEnvelope />
                </div>
                <div className="footer-contact-info">
                  <span className="footer-contact-label">Email Us</span>
                  <span className="footer-contact-value">{email}</span>
                </div>
              </a>

              {/* Address */}
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-contact-item"
              >
                <div className="footer-contact-icon" style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}>
                  <FaMapMarkerAlt />
                </div>
                <div className="footer-contact-info">
                  <span className="footer-contact-label">Visit Our Location</span>
                  <span className="footer-contact-value">{fullAddress}</span>
                </div>
              </a>
            </div>
          </div>

          {/* Column 3: Opening Hours - PREMIUM */}
          <div className="footer-column footer-hours-column">
            <div className="footer-section-header">
              <h3 className="footer-column-title" style={{ color: primaryColor }}>
                Opening Hours
              </h3>
              <p className="footer-section-subtitle">When we're open for you</p>
            </div>
            
            <div className="opening-hours-premium">
              {/* Status Badge */}
              <div className={`hours-status-badge ${status.open ? 'status-open' : 'status-closed'}`}>
                <FaCircle className="status-indicator-dot" />
                <span className="status-text">{status.open ? 'Open Now' : 'Closed'}</span>
              </div>

              {/* Current Day Card */}
              <div 
                className="hours-today-card"
                style={{ 
                  background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}08 100%)`,
                  borderColor: `${primaryColor}30`
                }}
              >
                <div className="hours-today-header">
                  <div className="hours-clock-wrapper" style={{ backgroundColor: primaryColor }}>
                    <FaClock />
                  </div>
                  <div className="hours-today-info">
                    <span className="hours-today-label">Today - {formatDay(today)}</span>
                    <span className="hours-today-value" style={{ color: primaryColor }}>
                      {todayHours}
                    </span>
                  </div>
                </div>
                <div className="hours-status-message">
                  {status.message}
                </div>
              </div>

              {/* Toggle Button */}
              <button 
                className="hours-toggle-btn-premium"
                onClick={() => setShowAllHours(!showAllHours)}
                style={{ 
                  borderColor: `${primaryColor}30`,
                  color: primaryColor 
                }}
              >
                <span>{showAllHours ? 'Hide' : 'View'} Full Schedule</span>
                {showAllHours ? <FaChevronUp /> : <FaChevronDown />}
              </button>

              {/* All Hours - Collapsible */}
              {showAllHours && (
                <div className="hours-all-days-premium">
                  {Object.entries(hours).map(([day, time]) => (
                    <div 
                      key={day} 
                      className={`hours-day-row-premium ${day === today ? 'hours-day-active' : ''}`}
                      style={day === today ? { 
                        background: `${primaryColor}10`,
                        borderLeftColor: primaryColor
                      } : {}}
                    >
                      <span className="hours-day-name-premium">{formatDay(day)}</span>
                      <span className="hours-day-dots"></span>
                      <span className="hours-day-time-premium">{time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Google Maps Section - Premium */}
        {showMapSection && (
          <div className="footer-map-section">
            <div className="footer-map-header">
              <h3 className="footer-map-title" style={{ color: primaryColor }}>
                Find Us On The Map
              </h3>
              <p className="footer-map-subtitle">Visit us at our location</p>
            </div>
            <div className="footer-map-container">
              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="450"
                style={{ border: 0, borderRadius: '20px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${businessName} Location`}
              ></iframe>
            </div>
          </div>
        )}

        {/* Footer Bottom */}
        <div className="footer-bottom-bar">
          <div className="footer-bottom-content">
            <p className="footer-copyright">
              © {currentYear} <span style={{ color: primaryColor }}>{businessName}</span>. Crafted with ❤️ | All Rights Reserved.
            </p>
            
            {/* Social Icons - Premium */}
            {showSocialLinks && socialLinks.length > 0 && (
              <div className="footer-social-section">
                <span className="footer-social-label">Follow Us</span>
                <div className="footer-social-icons-premium">
                  {socialLinks.map((link) => {
                    const IconComponent = link.icon;
                    return (
                      <a
                        key={link.platform}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-social-icon-premium"
                        style={{ '--social-color': link.color }}
                        aria-label={`Visit our ${link.platform}`}
                      >
                        <IconComponent />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
